import Button from '@/common/components/Button'
import MaskedTextField from '@/common/components/MaskedTextField'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import TextField from '@/common/components/TextField'
import { cpfMask } from '@/common/constants/masks.constant'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useAddContractEnvelopeWitness } from '@/features/privateCompanyLeadsFunnel/hooks/useAddContractEnvelopeWitness'
import {
  contractEnvelopeWitnessSchema,
  ContractEnvelopeWitnessValidationSchema,
} from '@/features/privateCompanyLeadsFunnel/schemas/contractEnvelope.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircleIcon, UserPlusIcon } from 'lucide-react'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

interface ModalAddWitnessProps {
  privateCompanyLeadId: string
}

export const ModalAddWitness = ({ privateCompanyLeadId }: ModalAddWitnessProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<ContractEnvelopeWitnessValidationSchema>({
    resolver: zodResolver(contractEnvelopeWitnessSchema),
  })

  const onSuccess = () => {
    setIsOpen(false)
    reset()
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLeadId] })
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLeadContractEnvelope', privateCompanyLeadId] })
  }

  const { mutate: addContractEnvelopeWitness, isPending: addContractEnvelopeWitnessIsPending } =
    useAddContractEnvelopeWitness({
      id: privateCompanyLeadId,
      onSuccess,
      onError: FormTransformers.errorsTransformer({ setError, setMessages }),
    })

  const onSubmit: SubmitHandler<ContractEnvelopeWitnessValidationSchema> = (data) => {
    addContractEnvelopeWitness(data)
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={(value) => {
          reset()
          setIsOpen(value)
        }}
        Trigger={
          <Button
            name="add_witness"
            className="flex items-center gap-x-2 bg-success-op hover:bg-success-hover disabled:hover:bg-success-op"
          >
            <p className="text-sm">Adicionar testemunhas</p>

            <UserPlusIcon className="size-4" />
          </Button>
        }
        useCancellableFooter
        title="Cadastrar testemunha"
        cancelButtonClassName="hidden"
        confirmButtonClassName="w-full bg-white-800 text-sm text-black-800"
        confirmButtonText="Cadastrar"
        confirmButtonIsLoading={addContractEnvelopeWitnessIsPending}
        onCancel={() => setIsOpen(false)}
        onConfirm={handleSubmit(onSubmit)}
        body={
          <form className="flex flex-col gap-y-4 text-white-800">
            <div className="flex items-center gap-x-2">
              <AlertCircleIcon strokeWidth={1} className="size-6 text-warning" />

              <p className="text-[11px] font-bold">Essa testemunha receberá o contrato para assinatura.</p>
            </div>

            <TextField
              label="Nome"
              className="w-full"
              {...register('contract.valid_envelope.witnesses.full_name')}
              errorMessage={errors?.contract?.valid_envelope?.witnesses?.full_name?.message}
              markAsRequired
            />

            <MaskedTextField
              label="Data de nascimento"
              mask="00/00/0000"
              className="w-full"
              {...register('contract.valid_envelope.witnesses.birthday')}
              errorMessage={errors?.contract?.valid_envelope?.witnesses?.birthday?.message}
              markAsRequired
            />

            <MaskedTextField
              label="CPF"
              mask={cpfMask}
              {...register('contract.valid_envelope.witnesses.cpf')}
              errorMessage={errors?.contract?.valid_envelope?.witnesses?.cpf?.message || undefined}
              markAsRequired
            />

            <TextField
              label="Email"
              className="w-full"
              {...register('contract.valid_envelope.witnesses.email')}
              errorMessage={errors?.contract?.valid_envelope?.witnesses?.email?.message}
              markAsRequired
            />
          </form>
        }
      />

      <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
    </>
  )
}
